import { BuildParamsTuple, RouteBuilder, RouteDefinitionMap } from './route-builder';

/**
 * This just wraps the route builder for now, however we can implement additional
 * functionality here in the future if needed.
 * e.g. `appRouter.navigateTo(...)` which will handle history + window.location.assign etc.
 */
export class AppRouter<RouteMap extends RouteDefinitionMap> {
    private readonly routeBuilder: RouteBuilder<RouteMap>;
    private readonly baseUrlWithoutSlash: string | null = null;

    public constructor(baseUrl: string | null) {
        this.routeBuilder = new RouteBuilder<RouteMap>();
        if (baseUrl) {
            this.baseUrlWithoutSlash = baseUrl.endsWith('/') ? baseUrl.substring(0, baseUrl.length - 1) : baseUrl;
        }
    }

    /**
     * Gets the full URL for the given route and parameters.
     *
     * @param subRoute - The route
     * @param paramsTuple - The parameters for the route
     * @returns The full URL as a string
     */
    public get<Route extends keyof RouteMap>(
        subRoute: Route,
        ...paramsTuple: BuildParamsTuple<RouteMap, Route>
    ): string {
        const base = this.baseUrlWithoutSlash ?? '';

        // The route should already have a leading slash
        return `${base}${this.getRoute(subRoute, ...paramsTuple)}`;
    }

    /**
     * Gets the route path for the given route and parameters.
     *
     * @param subRoute - The route
     * @param paramsTuple - The parameters for the route
     * @returns The route path as a string
     */
    public getRoute<Route extends keyof RouteMap>(
        subRoute: Route,
        ...paramsTuple: BuildParamsTuple<RouteMap, Route>
    ): string {
        const route = this.routeBuilder.build(subRoute, ...paramsTuple);
        if (route.startsWith('/')) {
            return route;
        }
        return `/${route}`;
    }
}
